import React, { useEffect, useState, useRef } from 'react';
import { BarChart3, Flame, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../api';



function Counter({ target, duration = 2, resetKey }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    started.current = false;
    setCount(0);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const step = target / (duration * 60);
          const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(Math.floor(start));
          }, 1000 / 60);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, resetKey]);

  return <span ref={ref}>{count.toLocaleString()}</span>;
}

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${className}`}
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function EpisodeProgress() {
  const [stories, setStories] = useState([]);
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get(`/api/stories`).then(r => setStories(r.data)).catch(() => {});
  }, []);

  // Auto-slide
  useEffect(() => {
    if (stories.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % stories.length);
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, [stories.length]);

  const goTo = (idx) => {
    setCurrent(idx);
    clearInterval(intervalRef.current);
    if (stories.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrent(prev => (prev + 1) % stories.length);
      }, 4000);
    }
  };

  if (stories.length === 0) return null;

  const story = stories[current];
  const upcoming = story.target_episodes - story.total_episodes;
  const daysToComplete = story.daily_release > 0 ? Math.ceil(upcoming / story.daily_release) : 0;

  return (
    <section id="progress" data-testid="episode-progress-section" className="py-20 sm:py-28 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neonGold/[0.02] to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <FadeIn className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-neonGold border border-neonGold/30 rounded-full mb-4 bg-neonGold/5">
            <BarChart3 className="inline w-3 h-3 mr-1 -mt-0.5" />
            Episode Tracker
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
            Track the Journey
          </h2>

          {/* Story selector with arrows */}
          {stories.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <button onClick={() => goTo((current - 1 + stories.length) % stories.length)}
                data-testid="tracker-prev-btn"
                className="w-10 h-10 rounded-full border border-neonGold/30 flex items-center justify-center text-neonGold hover:bg-neonGold/10 transition-all">
                <ChevronLeft size={20} />
              </button>
              <span className="font-heading text-xl font-semibold text-neonBlue min-w-[200px]" data-testid="tracker-story-name">
                {story.story_name}
              </span>
              <button onClick={() => goTo((current + 1) % stories.length)}
                data-testid="tracker-next-btn"
                className="w-10 h-10 rounded-full border border-neonGold/30 flex items-center justify-center text-neonGold hover:bg-neonGold/10 transition-all">
                <ChevronRight size={20} />
              </button>
            </div>
          )}

          {/* Dots */}
          {stories.length > 1 && (
            <div className="flex justify-center gap-2 mt-3" data-testid="tracker-dots">
              {stories.map((_, i) => (
                <button key={i} onClick={() => goTo(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-6 bg-neonGold' : 'w-1.5 bg-white/20'}`} />
              ))}
            </div>
          )}
        </FadeIn>

        <div className="grid sm:grid-cols-3 gap-6">
          <FadeIn delay={100}>
            <div data-testid="tracker-released" className="glass-card rounded-xl p-8 text-center h-full">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-neonBlue/10 border border-neonBlue/20 flex items-center justify-center">
                <Flame className="w-7 h-7 text-neonBlue" />
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-bold text-white mb-1">
                <Counter target={story.total_episodes} resetKey={current} />
              </div>
              <div className="text-gray-500 text-sm">Audio Episodes Released</div>
              <div className="mt-4 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  key={`bar-${current}`}
                  className="h-full bg-neonBlue rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${(story.total_episodes / story.target_episodes) * 100}%` }}
                />
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={200}>
            <div data-testid="tracker-upcoming" className="glass-card rounded-xl p-8 text-center h-full">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-neonGold/10 border border-neonGold/20 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-neonGold" />
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-bold text-white mb-1">
                <Counter target={upcoming > 0 ? upcoming : 0} resetKey={current} />
              </div>
              <div className="text-gray-500 text-sm">Upcoming Audio Episodes</div>
              <div className="mt-4 text-xs text-gray-600">
                {daysToComplete > 0 ? `~${daysToComplete} days to ${story.target_episodes} milestone` : 'Target reached!'}
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={300}>
            <div data-testid="tracker-daily" className="glass-card rounded-xl p-8 text-center h-full">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                <BarChart3 className="w-7 h-7 text-green-400" />
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-bold text-white mb-1">
                <Counter target={story.daily_release} duration={1} resetKey={current} />
              </div>
              <div className="text-gray-500 text-sm">Audio Eps Per Day</div>
              <div className="mt-4 text-xs text-green-500/60">Consistent daily uploads</div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
